import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type ValidatorResult } from 'ember-changeset-validations';
import {
  validateNumber,
  validatePresence,
} from 'ember-changeset-validations/validators';
import { customMessageValidator } from 'uplisting-frontend/validators/custom-message';
import type {
  IBookingDotComBedConfiguration,
  IBookingDotComSubroom,
} from 'uplisting-frontend/models/booking-dot-com-hotel-publication';
import {
  RoomType,
  MULTI_ROOM_TYPES,
  SINGLE_ROOM_TYPES,
} from 'uplisting-frontend/models/booking-dot-com-hotel-publication';

function validateMaxAdults(
  _key: string,
  newValue: unknown,
  _oldValue: unknown,
  _changes: Record<string, unknown>,
  content: Record<string, unknown>,
): ValidatorResult {
  const publication =
    content as unknown as ModelRegistry['booking-dot-com-hotel-publication'];
  const property = publication.property;

  if (!property?.suitability?.children) {
    return true;
  }

  const value = Number(newValue);
  const max = property.maximumCapacity;

  return Number.isInteger(value) && value >= 1 && value <= max;
}

function validateMaxChildren(
  _key: string,
  newValue: unknown,
  _oldValue: unknown,
  _changes: Record<string, unknown>,
  content: Record<string, unknown>,
): ValidatorResult {
  const publication =
    content as unknown as ModelRegistry['booking-dot-com-hotel-publication'];
  const property = publication.property;

  if (!property?.suitability?.children) {
    return true;
  }

  if (newValue === null || newValue === undefined || newValue === '') {
    return false;
  }

  const value = Number(newValue);
  const max = property.maximumCapacity;

  return Number.isInteger(value) && value >= 0 && value < max;
}

function validateMaxInfants(
  _key: string,
  newValue: unknown,
  _oldValue: unknown,
  _changes: Record<string, unknown>,
  content: Record<string, unknown>,
): ValidatorResult {
  const publication =
    content as unknown as ModelRegistry['booking-dot-com-hotel-publication'];
  const property = publication.property;

  if (!property?.suitability?.children) {
    return true;
  }

  if (newValue === null || newValue === undefined || newValue === '') {
    return false;
  }

  const value = Number(newValue);
  const max = property.maximumCapacity;

  return Number.isInteger(value) && value >= 0 && value < max;
}

const VALID_ROOM_TYPES: string[] = [...MULTI_ROOM_TYPES, ...SINGLE_ROOM_TYPES];

function validateSubrooms(
  _key: string,
  newValue: unknown,
  _oldValue: unknown,
  _changes: Record<string, unknown>,
  content: Record<string, unknown>,
): ValidatorResult {
  if (!Array.isArray(newValue) || newValue.length === 0) {
    return false;
  }

  const publication =
    content as unknown as ModelRegistry['booking-dot-com-hotel-publication'];
  const isMultiRoom = publication.isMultiRoom;

  if (isMultiRoom) {
    const allowedTypes = MULTI_ROOM_TYPES as string[];

    if (
      newValue.some(
        (s: IBookingDotComSubroom) => !allowedTypes.includes(s.type),
      )
    ) {
      return false;
    }

    if (
      !newValue.some(
        (s: IBookingDotComSubroom) => s.type === RoomType.BedroomSubroom,
      )
    ) {
      return false;
    }
  }

  if (!isMultiRoom && newValue.length !== 1) {
    return false;
  }

  if (
    !isMultiRoom &&
    (newValue[0] as IBookingDotComSubroom).type !== RoomType.GuestRoom
  ) {
    return false;
  }

  const isInvalid = newValue.some((subroom: IBookingDotComSubroom) => {
    if (!VALID_ROOM_TYPES.includes(subroom.type)) {
      return true;
    }

    const beds = Array.isArray(subroom.bed_configurations)
      ? subroom.bed_configurations
      : [];

    if (subroom.type === RoomType.LivingRoomSubroom && beds.length === 0) {
      return false;
    }

    if (beds.length === 0) {
      return true;
    }

    return subroom.bed_configurations.some(
      (bed: IBookingDotComBedConfiguration) => {
        const bedTypeId = Number(bed.bed_type_id);
        const bedCount = Number(bed.bed_count);
        return !(
          Number.isInteger(bedTypeId) &&
          Number.isInteger(bedCount) &&
          bedCount > 0
        );
      },
    );
  });

  return !isInvalid;
}

export const BookingDotComPublishPropertySettingsValidation = {
  size: customMessageValidator(
    'booking_dot_com_publish_property_settings_size',
    validateNumber({
      integer: true,
      positive: true,
      gt: 0,
    }),
  ),

  unitOfMeasurement: customMessageValidator(
    'booking_dot_com_publish_property_settings_unit_of_measurement',
    validatePresence({
      presence: true,
      ignoreBlank: true,
    }),
  ),

  maxAdults: customMessageValidator(
    'booking_dot_com_publish_property_settings_max_adults',
    validateMaxAdults,
  ),

  maxChildren: customMessageValidator(
    'booking_dot_com_publish_property_settings_max_children',
    validateMaxChildren,
  ),

  maxInfants: customMessageValidator(
    'booking_dot_com_publish_property_settings_max_infants',
    validateMaxInfants,
  ),

  subrooms: customMessageValidator(
    'booking_dot_com_publish_property_settings_subrooms',
    validateSubrooms,
  ),
};
